import React, { useEffect, useRef, useState } from 'react'
import './User.css'
import { Services } from '@/services/services'
import { NewPasswordFormData, password, SuperAdminEditUserForm, SuperAdminInsertUserForm, SuperAdminNewPasswordForm, UserFormData } from '@/components/forms/users/User'
import { CurrentSession, SessionService } from '@/services/repository/session'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { addServerErrors, onSubmit } from '@/components/forms/common/FormBase'
import { useForm, UseFormReturn } from 'react-hook-form'
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable'
import { formatDate } from '@/common/time'
import { useModal } from '@/components/modal/Modal'
import { BackButton } from '@/components/layout/NavBar'

export interface SimpleUser {
  id: string
  email: string
  username: string
  createdAt: Date
  isRelated?: boolean
}

export interface SuperAdminInsertPopupProps {
  services: Services,
  session: CurrentSession,
  onClose: () => void
}

export interface SuperAdminEditUsersPopupProps extends SuperAdminInsertPopupProps {
  id: string | undefined
}

export interface SuperAdminInsertUsersPopupProps extends SuperAdminInsertPopupProps { }

export const SuperAdminEditUsersPopup = ({ services, session, onClose, id }: SuperAdminEditUsersPopupProps) => {
  const form = useForm<UserFormData>()
  const [data, setData] = useState<UserFormData>()

  useEffect(() => {
    if (id === undefined) {
      return
    }
    setData(undefined)
    services.session.fetchWithToken(`/api/superadmin/users/byid/${id}`).then((r) => r.json()).then((data) => {
      setData(data)
      form.reset(data)
    })
  }, [id])

  const handle = (data: UserFormData): Promise<void> => {
    if (form === undefined) {
      return Promise.reject()
    }
    return services.session.fetchWithToken(`/api/superadmin/users/byid/${data.id}/update`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        addServerErrors(form, session.translates, erros)
        return
      }
      onClose()
    })
  }

  return data !== undefined ? <SuperAdminEditUserForm
    onSubmit={handle}
    services={services}
    form={form}
    translates={session.translates}
    formName='user'
    readOnly={false}
  /> : <div className="spinner"></div>
}

export const SuperAdminInsertUsersPopup = ({ services, session, onClose }: SuperAdminInsertUsersPopupProps) => {
  const form = useForm<UserFormData>()

  const handle = (data: UserFormData): Promise<void> => {
    if (form === undefined) {
      return Promise.reject()
    }
    delete data.id
    return services.session.fetchWithToken(`/api/superadmin/users/new`, {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        addServerErrors(form, session.translates, erros)
        return
      }
      form.reset()
      onClose()
    })
  }

  return <SuperAdminInsertUserForm
    onSubmit={handle}
    services={services}
    form={form}
    translates={session.translates}
    formName='user'
    readOnly={false}
  />
}

export interface SuperAdminRemoveUsersPopupProps extends SuperAdminInsertPopupProps {
  user: SimpleUser | undefined
}


export const SuperAdminRemoveUsersPopup = ({ services, session, onClose, user }: SuperAdminRemoveUsersPopupProps) => {
  const form = useForm<UserFormData>()
  const [error, setError] = useState<boolean>(false)

  useEffect(() => {
    setError(false)
  }, [user?.id])

  if (user === undefined) {
    return null
  }

  const remove = (): Promise<void> => {
    return services.session.fetchWithToken(`/api/superadmin/users/byid/${user.id}/delete`, {
      method: 'DELETE'
    }).then(async (r) => {
      if (r.status !== 200) {
        setError(true)
      }
      setError(false)
      onClose()
    })
  }
  return <div className='dialog'>
    <div className='dialog-text'>{session.translates.for('elements.table.users.remove_user_ask').replace('{username}', user.username)}</div>
    <div className='dialog-btns'>
      <div className='dialog-btn' onClick={() => onClose()}>
        <i className='svgfont-no' />
        {session.translates.for('elements.dialog.yesno.no')}
      </div>
      <div className='dialog-btn' onClick={() => remove()}>
        <i className='svgfont-yes' />
        {session.translates.for('elements.dialog.yesno.yes')}
      </div>
      {error ? <div className='dialog-error'>{session.translates.for('elements.table.users.remove_user_error')}</div> : null}
    </div>
  </div>
}

export interface SuperAdminNewPasswordProps extends SuperAdminInsertPopupProps {
  id: string | undefined
}

export const SuperAdminNewPasswordPopup = ({ services, session, onClose, id }: SuperAdminNewPasswordProps) => {
  const form = useForm<NewPasswordFormData>()

  const handle = (data: NewPasswordFormData): Promise<void> => {
    if (form === undefined) {
      return Promise.reject()
    }
    return services.session.fetchWithToken(`/api/superadmin/users/byid/${id}/new-password`, {
      method: 'PUT',
      body: JSON.stringify({
        password: data.password
      }),
    }).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        addServerErrors(form, session.translates, erros)
        return
      }
      form.reset()
      onClose()
    })
  }

  return <SuperAdminNewPasswordForm
    onSubmit={handle}
    services={services}
    form={form}
    translates={session.translates}
    readOnly={false}
    formName='user'
  />
}

// --- RELATION TO USER GROUPS -----

interface SimpleUserGroup {
  id: string
  name: string
  isRelated?: boolean
}

const RelatedUserGroupsPage = ({
  services,
  session,
  userId,
  onClose
}: {
  services: Services,
  session: CurrentSession,
  userId: string | undefined,
  onClose: () => void
}) => {
  const searchTableRef = useRef<SearchTableRef>(null)

  const tableParams: SearchTableParams<SimpleUserGroup> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleUserGroup>> => {
      return services.session.fetchWithToken(
        `/api/superadmin/users/byid/${userId}/groups/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`
      ).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => <></>,
    header: () => <>
      <div className='hcol'></div>
      <div className='hcol'>{session.translates.for('elements.table.usergroups.name')}</div>
    </>,
    row: (row, state, setState) => <>
      <div className='col icocol' onClick={() => {
        setState(state => {
          if (state === undefined) return
          state.data = state.data?.map(item =>
            item.id === row.id ? { ...item, isRelated: undefined } : item
          )
          return { ...state }
        })

        services.session.fetchWithToken(`/api/superadmin/users/byid/${userId}/relations/groups/${row.id}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id,
            related: !row.isRelated
          }),
        }).then(resp => {
          setState(state => {
            if (state === undefined) return
            state.data = state.data?.map(item =>
              item.id === row.id ? { ...item, isRelated: resp.ok ? !row.isRelated : row.isRelated } : item
            )
            return { ...state }
          })
        })
      }}>
        {row.isRelated === undefined
          ? <div className='spinner'></div>
          : row.isRelated
            ? <i className='svgfont-checkbox' />
            : <i className='svgfont-checkbox-blank' />
        }
      </div>
      <div className='col'>{row.name}</div>
    </>,
    translates: session.translates
  }

  return <SearchTable ref={searchTableRef} {...tableParams} />
}


// ---- CSV -------

const fetchCSV = async ({ services, session }: { services: Services, session: CurrentSession }) => {
  try {
    const response = await services.session.fetchWithToken('/api/superadmin/users/csv', {
      method: 'GET',
      headers: {
        'Accept': 'text/csv',
      },
    })
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`)
    }
    const csvData = await response.text()
    const blob = new Blob([csvData], { type: 'text/csv' })
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = 'users.csv'
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    console.error('Error fetching CSV:', error)
  }
}

export const SuperAdminUsersPage = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const searchTableRef = useRef<SearchTableRef>(null)
  const { openModal } = useModal()

  const tableParams: SearchTableParams<SimpleUser> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleUser>> => {
      return services.session.fetchWithToken(`/api/superadmin/users/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => <>
      <div className='tool' onClick={() => openModal((props) => SuperAdminInsertUsersPopup({...props, services, session}))}><i className='svgfont-add' /></div>
      <div className='tool' onClick={() => fetchCSV({ services, session })}><i className='svgfont-csv' /></div>
    </>,
    header: () => <>
      <div className='hcol desktop-only'>{session.translates.for('elements.table.users.createdAt')}</div>
      <div className='hcol desktop-only'>{session.translates.for('elements.table.users.email')}</div>
      <div className='hcol'>{session.translates.for('elements.table.users.username')}</div>
      <div className='hcol'></div>
      <div className='hcol'></div>
      <div className='hcol'></div>
      <div className='hcol'></div>
    </>,
    row: (row) => <>
      <div className='col desktop-only'>{formatDate(new Date(row.createdAt))}</div>
      <div className='col desktop-only'>{row.email}</div>
      <div className='col'>{row.username}</div>
      <div className='col icocol' onClick={() => openModal((props) => SuperAdminNewPasswordPopup({...props, id: row.id, services, session}))}>
        <i className='svgfont-password' />
      </div>
      <div className='col icocol' onClick={() => openModal((props) => RelatedUserGroupsPage({...props, userId: row.id, services, session}))}>
        <i className='svgfont-users' />
      </div>
      <div className='col icocol' onClick={() => openModal((props) => SuperAdminEditUsersPopup({...props, id: row.id, services, session}))}>
        <i className='svgfont-edit' />
      </div>
      <div className='col icocol' onClick={() => openModal((props) => SuperAdminRemoveUsersPopup({...props, user: row, services, session}))}>
        <i className='svgfont-remove' />
      </div>
    </>,
    translates: session.translates
  }

  return <LoggedInPageLayout pageName='superadmin-users' services={services} session={session}
      navbarLeft={<BackButton services={services} translates={session.translates} />}>
    <SearchTable ref={searchTableRef} {...tableParams} />
  </LoggedInPageLayout>
}
